import { SelectTypes } from '@genial/components';
import { IconographyOptions, IconographyTypes } from '@genial/icons';
import type {
  ResponsesGetReportingFileDocumentTypeItemResponse,
  ResponsesGetReportingFileDocumentTypesResponse,
  ResponsesGetReportingFileIconographyResponse,
  ResponsesGetReportingFileParameterDefinitionResponse,
  ResponsesGetReportingFileSubCategoryResponse,
} from '@genial/services/dist/models';

import { mapSupportedPeriodsToOptions } from './periods';

export interface ReportGeneratorParameterDefinition {
  name: string;
  label: string;
  required: boolean;
  options: SelectTypes.Option[];
}

export interface ReportGeneratorIconography {
  name: IconographyTypes.Option;
  color: string;
  size: 'xxs' | 'xs' | 'sm' | 'md' | 'l';
  backgroundColor: string;
}

export interface ReportGeneratorDocumentType {
  id: string;
  categoryId: string;
  subCategoryId: string;
  label: string;
  description: string;
  periodOptions: SelectTypes.Option[];
  parameters: ReportGeneratorParameterDefinition[];
}

export interface ReportGeneratorSubCategory {
  id: string;
  categoryId: string;
  label: string;
  description: string;
  iconography: ReportGeneratorIconography;
  documentTypes: ReportGeneratorDocumentType[];
  canRequest: boolean;
}

export interface ReportGeneratorCategory {
  id: string;
  label: string;
  description?: string;
  subCategories: ReportGeneratorSubCategory[];
}

export const REPORT_GENERATOR_CATEGORY_FILTER_ALL = 'all';
export const REPORT_GENERATOR_PROFITABILITY_DOCUMENT_TYPE_ID = 6;
export const REPORT_GENERATOR_FEE_SETTLEMENT_DOCUMENT_TYPE_ID = 7;

export const isProfitabilityDocumentType = (documentTypeId?: number | null): boolean =>
  documentTypeId === REPORT_GENERATOR_PROFITABILITY_DOCUMENT_TYPE_ID;

export const isFeeSettlementDocumentType = (documentTypeId?: number | null): boolean =>
  documentTypeId === REPORT_GENERATOR_FEE_SETTLEMENT_DOCUMENT_TYPE_ID;

const DEFAULT_ICON: IconographyTypes.Option = 'InvestmentChart';
const DEFAULT_ICON_COLOR = 'brand.primary.principal';
const DEFAULT_ICON_BG = 'brand.tertiary.lightest';
const DEFAULT_ICON_SIZE: ReportGeneratorIconography['size'] = 'sm';

const mapIconName = (name: string | null | undefined): IconographyTypes.Option => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return DEFAULT_ICON;
  }

  return IconographyOptions.includes(trimmed as IconographyTypes.Option)
    ? (trimmed as IconographyTypes.Option)
    : DEFAULT_ICON;
};

const mapIconography = (
  iconography: ResponsesGetReportingFileIconographyResponse | null | undefined
): ReportGeneratorIconography => {
  const size = iconography?.size?.trim();
  const resolvedSize: ReportGeneratorIconography['size'] =
    size === 'xxs' || size === 'xs' || size === 'sm' || size === 'md' || size === 'l'
      ? size
      : DEFAULT_ICON_SIZE;

  return {
    name: mapIconName(iconography?.name),
    color: iconography?.color?.trim() || DEFAULT_ICON_COLOR,
    size: resolvedSize,
    backgroundColor: iconography?.backgroundColor?.trim() || DEFAULT_ICON_BG,
  };
};

const mapParameterDefinition = (
  parameter: ResponsesGetReportingFileParameterDefinitionResponse
): ReportGeneratorParameterDefinition | undefined => {
  const name = parameter.name?.trim();
  if (!name) {
    return undefined;
  }

  return {
    name,
    label: parameter.label?.trim() || name,
    required: Boolean(parameter.required),
    options:
      parameter.options?.reduce<SelectTypes.Option[]>((options, option) => {
        const value = option.value?.trim();
        if (!value) {
          return options;
        }

        options.push({
          value,
          label: option.label?.trim() || value,
        });

        return options;
      }, []) ?? [],
  };
};

const mapDocumentType = (
  documentType: ResponsesGetReportingFileDocumentTypeItemResponse,
  categoryId: string,
  subCategoryId: string
): ReportGeneratorDocumentType => {
  const id = `${documentType.id}`;

  return {
    id,
    categoryId,
    subCategoryId,
    label: documentType.title?.trim() || id,
    description: documentType.description?.trim() || '',
    periodOptions: mapSupportedPeriodsToOptions(documentType.supportedPeriods),
    parameters:
      documentType.parameters?.reduce<ReportGeneratorParameterDefinition[]>(
        (parameters, parameter) => {
          const mapped = mapParameterDefinition(parameter);
          if (mapped) {
            parameters.push(mapped);
          }
          return parameters;
        },
        []
      ) ?? [],
  };
};

const mapSubCategory = (
  subCategory: ResponsesGetReportingFileSubCategoryResponse,
  categoryId: string
): ReportGeneratorSubCategory => {
  const id = `${subCategory.subCategoryId}`;
  const documentTypes = (subCategory.documentTypes ?? []).map((documentType) =>
    mapDocumentType(documentType, categoryId, id)
  );

  return {
    id,
    categoryId,
    label: subCategory.title?.trim() || id,
    description: subCategory.description?.trim() || '',
    iconography: mapIconography(subCategory.iconography),
    documentTypes,
    canRequest: documentTypes.length > 0,
  };
};

export const mapReportGeneratorCategoriesFromApi = (
  categories: ResponsesGetReportingFileDocumentTypesResponse[] | null | undefined
): ReportGeneratorCategory[] => {
  if (!categories?.length) {
    return [];
  }

  return categories.map((category) => {
    const id = `${category.categoryId}`;
    const subCategories = (category.subCategories ?? []).map((subCategory) =>
      mapSubCategory(subCategory, id)
    );

    return {
      id,
      label: category.title?.trim() || id,
      description: category.description?.trim() || undefined,
      subCategories,
    };
  });
};

const flattenReportGeneratorSubCategories = (
  categories: ReportGeneratorCategory[]
): ReportGeneratorSubCategory[] => categories.flatMap((category) => category.subCategories);

const flattenReportGeneratorDocumentTypes = (
  categories: ReportGeneratorCategory[]
): ReportGeneratorDocumentType[] =>
  flattenReportGeneratorSubCategories(categories).flatMap(
    (subCategory) => subCategory.documentTypes
  );

export const getSubCategoriesByCategoryFilter = (
  categories: ReportGeneratorCategory[],
  categoryFilterId: string = REPORT_GENERATOR_CATEGORY_FILTER_ALL
): ReportGeneratorSubCategory[] => {
  if (categoryFilterId === REPORT_GENERATOR_CATEGORY_FILTER_ALL) {
    return flattenReportGeneratorSubCategories(categories);
  }

  return categories.find((category) => category.id === categoryFilterId)?.subCategories ?? [];
};

export const getDocumentTypesByCategoryId = (
  categories: ReportGeneratorCategory[],
  categoryId: string | undefined
): ReportGeneratorDocumentType[] => {
  if (!categoryId) {
    return [];
  }

  return (
    categories
      .find((category) => category.id === categoryId)
      ?.subCategories.flatMap((subCategory) => subCategory.documentTypes) ?? []
  );
};

export const findReportGeneratorCategory = (
  categories: ReportGeneratorCategory[],
  categoryId: number | string | undefined | null
): ReportGeneratorCategory | undefined => {
  if (categoryId === undefined || categoryId === null || categoryId === '') {
    return undefined;
  }

  return categories.find((category) => category.id === `${categoryId}`);
};

export const findReportGeneratorSubCategory = (
  categories: ReportGeneratorCategory[],
  subCategoryId: string | undefined
): ReportGeneratorSubCategory | undefined => {
  if (!subCategoryId) {
    return undefined;
  }

  return flattenReportGeneratorSubCategories(categories).find(
    (subCategory) => subCategory.id === subCategoryId
  );
};

export const findReportGeneratorDocumentType = (
  categories: ReportGeneratorCategory[],
  documentTypeId: string | undefined
): ReportGeneratorDocumentType | undefined => {
  if (!documentTypeId) {
    return undefined;
  }

  return flattenReportGeneratorDocumentTypes(categories).find(
    (documentType) => documentType.id === documentTypeId
  );
};

export const formatReportGeneratorTypeLabel = (
  categoryLabel: string | undefined,
  documentTypeLabel: string | null | undefined
): string => {
  const category = categoryLabel?.trim() || '';
  const documentType = documentTypeLabel?.trim() || '';

  if (category && documentType) {
    return `${category} - ${documentType}`;
  }

  return documentType || category || '--';
};

export const toDocumentTypeSelectOptions = (
  documentTypes: ReportGeneratorDocumentType[]
): SelectTypes.Option[] =>
  documentTypes.map((documentType) => ({
    value: documentType.id,
    label: documentType.label,
  }));

const isDocumentTypeVisibleByFlags = (
  documentTypeId: number,
  showEquity: boolean,
  showProfitability: boolean,
  showFixedFee: boolean
): boolean => {
  if (isFeeSettlementDocumentType(documentTypeId)) {
    return showFixedFee;
  }

  if (isProfitabilityDocumentType(documentTypeId)) {
    return showProfitability;
  }

  return showEquity;
};

export const filterReportGeneratorCategoriesByFlags = (
  categories: ReportGeneratorCategory[],
  showEquity: boolean,
  showProfitability: boolean,
  showFixedFee = false
): ReportGeneratorCategory[] =>
  categories.reduce<ReportGeneratorCategory[]>((visibleCategories, category) => {
    const subCategories = category.subCategories.flatMap((subCategory) => {
      if (!subCategory.documentTypes.length) {
        return [subCategory];
      }

      const documentTypes = subCategory.documentTypes.filter((documentType) =>
        isDocumentTypeVisibleByFlags(
          Number(documentType.id),
          showEquity,
          showProfitability,
          showFixedFee
        )
      );

      if (!documentTypes.length) {
        return [];
      }

      return [{ ...subCategory, documentTypes, canRequest: true }];
    });

    if (!subCategories.length) {
      return visibleCategories;
    }

    visibleCategories.push({ ...category, subCategories });
    return visibleCategories;
  }, []);
